<template>
  <nav>
    <v-row
      v-if="alertMsg != ''"
      class="fill-height"
    >
      <v-col
        align-self="center"
        class="text-center"
        cols="12"
        sm="8"
        md="6"
        lg="4"
        offset-sm="2"
        offset-md="3"
        offset-lg="4"
      >
        <v-img
          src="../../assets/image3.png"
          width="100%"
          max-width="400px"
          class="mx-auto mb-10"
        />
        <p class="text-h5 font-weight-bold">
          {{ alertType }}
        </p>
        <p class="body-1">
          {{ alertMsg }}
        </p>
      </v-col>
    </v-row>
    <v-row
      v-else
      class="fill-height"
      align="center"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
        align="center"
      >
        <v-card
          v-if="showRegistrationForm"
          max-width="600"
          elevation="0"
          rounded="xxl"
          color="background"
        >
          <LanguageSwitcher @languageSelected = "updateLocale"/>
          <v-card-title
            :class="mobile() ? 'text-h5 font-weight-bold text-center justify-center no-break' : 'mx-10  text-h5 font-weight-bold text-center justify-center no-break'"
          >
            {{ $t('joinSpacePageTitle') }}
          </v-card-title>
          <v-card-subtitle class="text-body-2 pt-4 no-break">
            {{ $t('joinSpacePageText') }}
          </v-card-subtitle>
          <v-card-text>
            <v-form
              ref="form"
              v-model="valid"
              @submit.prevent
              @keyup.enter="registerUserJoinSpace"
              class="checkbox-align-start"
            >
              <v-text-field
                v-model="email"
                data-testid="email-input"
                disabled
                solo-inverted
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountEmailPlaceholder')"
                :rules="emailRules"
              />
              <br>
              <v-text-field
                v-model="firstName"
                data-testid="first-name-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountFirstNamePlaceholder')"
                :rules="firstNameRules"
              />
              <v-text-field
                v-model="lastName"
                data-testid="last-name-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('createAccountLastNamePlaceholder')"
                :rules="lastNameRules"
              />
              <br>
              <v-text-field
                v-model="password"
                data-testid="password-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('createAccountPasswordPlaceholder')"
                :rules="passwordRules"
                @click:append="showPassword = !showPassword"
              />
              <v-text-field
                v-model="confirmPassword"
                data-testid="password-confirm-input"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPasswordConfirm ? 'text' : 'password'"
                :placeholder="$t('createAccountConfirmPasswordPlaceholder')"
                :rules="passwordRules.concat(passwordConfirmationRule)"
                @click:append="showPasswordConfirm = !showPasswordConfirm"
              />
              <v-checkbox
                v-model="acceptedGdpr"
                data-testid="gdpr-input"
                color="accent"
                @change="gdpr($event)"
              >
                <template v-slot:label>
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    ref="link"
                    v-html="$t('checkboxGdprLabel', {
                      'terms-of-services': `<a target='_blank' href='/terms-of-service'>${ $t('termsOfServices') }</a>`,
                      'privacy-policy': `<a target='_blank' href='/privacy-policy'>${ $t('privacyPolicy') }</a>`
                    })" 
                  />
                </template>
              </v-checkbox>
              <v-card-actions>
                <v-btn
                  data-testid="submit-button"
                  :disabled="buttonDisabled"
                  color="accent"
                  large
                  block
                  class="rounded-xxl text-none"
                  @click="registerUserJoinSpace"
                >
                  {{ $t('signUp') }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </nav>
</template>
<script>
import { ruleRequired, emailRuleValid, ruleValidMin2Max70Characters,  passwordRuleValid, passwordRuleCharactersValid } from '../../rules/formRules.js';
import i18n from '../../plugins/i18n'
import { joinSpaceCheckUserExists, joinSpaceRegisterUser,getSpaceByURL,joinSpace } from '../../api/spaceApi'
import { loginUser } from '../../api/userApi'
import {loginAuth} from '../../auth/auth.js'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin';
import LanguageSwitcher from '../../components/LanguageSwitcher'

export default {
    components: {
        LanguageSwitcher
    },
    mixins: [repeatedMethodsMixin],
    data(){
        return{
            valid: true,
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
            acceptedGdpr: false,
            showPassword: false,
            showPasswordConfirm: false,
            alertType: '',
            alertMsg: '',
            buttonDisabled: true,
            signature: this.$route.params.signature,
            showRegistrationForm: false,
            languageRuleActive: true
         }
    },
    computed: {
        firstNameRules() {
            return this.languageRuleActive ? ruleRequired('labelFirstName').concat(ruleValidMin2Max70Characters('labelFirstName')) : []
        },
        lastNameRules() {
            return this.languageRuleActive ? ruleRequired('labelLastName').concat(ruleValidMin2Max70Characters('labelLastName')) : []
        },
        emailRules() {
            return this.languageRuleActive ? ruleRequired('labelEmail').concat(emailRuleValid) : []
        },
        passwordRules() {
            return this.languageRuleActive ? ruleRequired('labelPassword').concat(passwordRuleCharactersValid, passwordRuleValid) : []
        },
        passwordConfirmationRule() {
            return () =>
                this.password === this.confirmPassword || i18n.t('fieldErrorMatch', {'field': i18n.t('labelPassword') })
        },
    },
    watch: {
      '$i18n.locale': function() {
        setTimeout(() => {
            this.$refs['link'].children[0].addEventListener('click', function(event) {
                event.stopImmediatePropagation()
            })
            this.$refs['link'].children[1].addEventListener('click', function(event) {
                event.stopImmediatePropagation()
            })
          }, 100)
      }
    },
    async created(){
        //check if someone is logged in
        if (window.$cookies.get("token") == null) {
           //make a call to decide if user is new or existing one
            try {
                const response = await joinSpaceCheckUserExists(this.signature) 
                if(response.status == 200) {
                    if(response.data.meta.code == 'userFound'){
                        this.$router.push({name: 'CustomLogin', params: {signature: this.signature}})
                    }else {
                        this.email = response.data.meta.email
                        this.showRegistrationForm = true
                        this.$nextTick(function () {
                            this.showGdpr()
                        })
                    }
                }
            } catch (e) {
                if(e.response){
                 if(e.response.status == 400){
                    this.alertValue = true
                    for(let i = 0; i < e.response.data.errors.length; i++){
                        if(e.response.data.errors[i].detail == 'Wrong signature.'){
                              this.alertType = i18n.t('sorry')
                              this.alertMsg = i18n.t('wrongSignature')
                        }else{
                          this.alertType = i18n.t('error')
                          this.alertMsg =  i18n.t('serverError')
                        }
                    }
                  }
                }
            }   
        }else {
            if(this.$route.params.signature){
                this.joinSpace(false)
            }
        }
    },
    methods: {
        gdpr: function(e) {
            e === true ? this.buttonDisabled = false : this.buttonDisabled = true
        },
        showGdpr() {
            this.$refs['link'].children[0].addEventListener('click', function(event) {
                event.stopImmediatePropagation()
            })
            this.$refs['link'].children[1].addEventListener('click', function(event) {
                event.stopImmediatePropagation()
            })
        },
        async registerUserJoinSpace(){
            this.$refs.form.validate()
            if(this.valid){
                this.buttonDisabled = true
                try {
                    let data = {
                        data: {
                            type: "users", 
                            attributes: {
                              first_name: this.firstName.trim(),
                              last_name: this.lastName.trim(),
                              email: this.email.trim(),
                              email_confirmation: this.email.trim(),
                              password: this.password.trim(),
                              password_confirmation: this.confirmPassword.trim(),
                              accepted_gdpr: this.acceptedGdpr,
                              language: window.$cookies.get('language') ? window.$cookies.get('language') : 'en'
                            }
                        }
                    };
                    const response = await joinSpaceRegisterUser(this.signature, data)
                    if(response.status == 201) {
                        //login user
                        try {
                            let data = {
                                email: this.email.trim(),
                                password: this.password.trim()
                            };
                            const response = await loginUser(data)
                            if(response.status == 200) {
                                loginAuth(response.data.meta.access_token, response.data.data)

                                this.joinSpace(true)
                            }
                        } catch (e) {
                            if(e.response){
                                this.$router.push({name: 'CustomLogin', params: {signature: this.signature}})
                            }
                        }
                    }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'The password must contain at least one special character!'){
                                    this.alertType = i18n.t('error')
                                    this.alertMsg = i18n.t('resetPasswordInvalidCharacter')
                                }else{
                                    this.alertType = i18n.t('error')
                                    this.alertMsg =  i18n.t('serverError')
                                }
                            }
                        }else{
                          this.alertType = i18n.t('error')
                          this.alertMsg =  i18n.t('serverError')
                        }
                    }
                }
                this.buttonDisabled = true
            }
        },
       async joinSpace(isNew){
            var space = window.location.host.split('.')[0];
            try {
                const response = await joinSpace(this.signature) 
                if(response.status == 200) {
                    const response_space = await getSpaceByURL(space)
                    this.$store.dispatch("getSubscribedPlan", response_space.data.data).catch(error => {
                      if (error.response.status == 403) {
                        this.$router.push({name: 'PageAccessDenied'})
                      }
                      if (error.response.status == 404) {
                        this.$router.push({name: 'PageNotFound'})
                      }
                    })

                    this.$store.dispatch("setBrokenWorkflow", response_space.data.data.attributes.workflow_warning)
                    
                    if(response.data.meta.code == 'invitationAccepted'){
                        localStorage.space = JSON.stringify(response_space.data.data.attributes)
                        if(response_space.data.data.attributes.role == 'participant' || response_space.data.data.attributes.role == 'guest'){
                            if(isNew == true){
                                this.$router.push({name: 'ActivitiesParticipant', params: {acceptedInvitationRegistered: true}})
                            }else{
                                this.$router.push({name: 'ActivitiesParticipant', params: {acceptedInvitation: true}})
                            }
                        }else{
                            if(isNew == true){
                                this.$router.push({name: 'Dashboard', params: {acceptedInvitationRegistered: true}})
                            }else{
                                this.$router.push({name: 'Dashboard', params: {acceptedInvitation: true}})
                            }
                        }
                    } else {
                        if(response_space.data.data.attributes.role == 'participant' || response_space.data.data.attributes.role == 'guest'){
                            this.$router.push({name: 'ActivitiesParticipant'})
                        } else {
                            this.$router.push({name: 'Dashboard'})
                        }
                    }
                }
            } catch (e) {
                if(e.response){
                    this.alertType = i18n.t('error')
                    this.alertMsg =  i18n.t('serverErrorWithoutTryAgain')
                    this.buttonDisabled = false
                }
            }   
        },
        updateLocale() {
            this.languageRuleActive = false
            this.$nextTick(() => {
              this.languageRuleActive = true
            })
        },
    }
}
</script>
